import React, { Fragment, memo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { COMPONENTS } from 'site-modules/personalized-search/constants/components';
import { SEARCH_COMPONENTS_MAP } from 'site-modules/personalized-search/search-components-map';
import { createComponentPath } from 'site-modules/personalized-search/utils/create-component-path';
import { StubComponent } from 'site-modules/personalized-search/components/stub-component/stub-component';
import { useSearchResults } from 'site-modules/personalized-search/hooks/use-search-results';

const GROUPS_CONFIG = {
  root: [
    {
      components: [
        COMPONENTS.OVERVIEW,
        COMPONENTS.DEALERSHIP,
        COMPONENTS.ANCHOR,
        COMPONENTS.VIN,
        COMPONENTS.LINKS,
        COMPONENTS.ANSWER,
      ],
      classNamesCb: () => 'components-group base-indent pt-1_5',
    },
    {
      components: [COMPONENTS.INVENTORY],
      classNamesCb: isMobile => `${isMobile ? '' : 'base-indent'} components-group`,
    },
  ],
};

const splitComponentsByGroup = (config, componentParams, parentComponentName = 'root') => {
  const componentGroups = config?.[parentComponentName] || [];

  let configGroupIndex;

  return componentParams.components?.reduce((groupedComponents, component) => {
    const currentComponentConfigGroupIndex = componentGroups.findIndex(componentGroup =>
      componentGroup.components.includes(component)
    );

    if (configGroupIndex === currentComponentConfigGroupIndex) {
      const activeGroup = groupedComponents[groupedComponents.length - 1];
      activeGroup.components.push(component);
    } else {
      configGroupIndex = currentComponentConfigGroupIndex;
      groupedComponents.push({ ...componentGroups[configGroupIndex], components: [component] });
    }

    return groupedComponents;
  }, []);
};

export const SearchRenderer = memo(
  ({
    componentParams = null,
    parentComponentName = '',
    componentPath = '',
    order = null,
    gradientGroupsConfig = GROUPS_CONFIG,
    isMobile = false,
    searchComponentMap = undefined,
    ...props
  }) => {
    const { isInventoryOnly } = useSearchResults();

    if (!componentParams) {
      return null;
    }

    const componentsByGroup = splitComponentsByGroup(gradientGroupsConfig, componentParams);
    const map = searchComponentMap || SEARCH_COMPONENTS_MAP;

    return (
      <>
        {componentsByGroup?.map(({ components, classNamesCb }, index) => {
          const componentsMap = components?.map(componentName => {
            const fullComponentName = parentComponentName ? `${parentComponentName}.${componentName}` : componentName;
            const nextComponentPath = createComponentPath(componentPath, componentName, order);

            const Component = map[fullComponentName]?.component || StubComponent;
            const creativeId = map[fullComponentName]?.creativeId;

            return (
              <Component
                key={nextComponentPath}
                {...props}
                parentComponentName={parentComponentName}
                componentPath={nextComponentPath}
                componentParams={componentParams[componentName]}
                creativeId={creativeId}
                isMobile={isMobile}
              />
            );
          });
          const classNames = classNamesCb?.(isMobile, components, isInventoryOnly);

          return classNames ? (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} className={classnames(classNames)}>
              {componentsMap}
            </div>
          ) : (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={index}>{componentsMap}</Fragment>
          );
        })}
      </>
    );
  }
);

SearchRenderer.displayName = 'SearchRenderer';
SearchRenderer.propTypes = {
  componentParams: PropTypes.shape({}),
  parentComponentName: PropTypes.string,
  componentPath: PropTypes.string,
  order: PropTypes.number,
  gradientGroupsConfig: PropTypes.shape({}),
  isMobile: PropTypes.bool,
  searchComponentMap: PropTypes.objectOf(
    PropTypes.shape({
      name: PropTypes.string,
      component: PropTypes.elementType,
      creativeId: PropTypes.string,
    })
  ),
};
